import {
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/view/components/ui/dialog";
import { Button } from "@/view/components/ui/button";
import {
  ChevronLeft,
  ChevronRight,
  FileText,
  Check,
  Image as ImageIcon,
  Trash2,
} from "lucide-react";
import useListDocumentsModal from "./useListDocumentsModal";
import { cn } from "@/app/utils/cn";
import { useRef } from "react";

interface ListDocumentsModalProps {
  onContinue: () => void;
  onBack: () => void;
}

export default function ListDocumentsModal({
  onContinue,
}: ListDocumentsModalProps) {
  const {
    documents,
    handleAddFile,
    handleRemoveFile,
    submitModal,
    selectedDocumentId,
    handleSelectDocument,
    handleBackToList,
    isLoading,
  } = useListDocumentsModal({
    onContinue,
  });

  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const triggerFileInput = (id: string, side?: "frente" | "verso") => {
    const key = side ? `${id}-${side}` : id;
    fileInputRefs.current[key]?.click();
  };

  const onFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    documentId: string,
    side?: "frente" | "verso",
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      handleAddFile(documentId, file, side);
    }
    event.target.value = "";
  };

  const selectedDocument = documents.find(
    (doc) => doc.id === selectedDocumentId,
  );

  const renderUploadBox = (
    docId: string,
    label: string,
    side?: "frente" | "verso",
  ) => {
    const file = selectedDocument?.files.find((f) => f.side === side);

    return (
      <div className="flex-1">
        <div
          className="relative flex h-32 w-full cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100"
          onClick={() => triggerFileInput(docId, side)}
        >
          {file ? (
            <>
              {file.preview ? (
                <img
                  src={file.preview}
                  alt={label}
                  className="h-full w-full rounded-lg object-contain p-2"
                />
              ) : (
                <div className="flex flex-col items-center text-gray-500">
                  <FileText className="mb-2 h-8 w-8" />
                  <span className="text-xs font-semibold">
                    {file.file.name}
                  </span>
                </div>
              )}
              <div className="absolute right-2 top-2 rounded-full bg-white p-1 shadow-sm">
                <Check className="h-4 w-4 text-green-500" />
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center text-gray-400">
              <ImageIcon className="mb-2 h-8 w-8 opacity-50" />
              <span className="text-sm font-semibold text-black01">
                Enviar imagem
              </span>
              <span className="text-xs">Máximo de 4 MB</span>
            </div>
          )}

          <input
            type="file"
            ref={(el) =>
              (fileInputRefs.current[side ? `${docId}-${side}` : docId] = el)
            }
            className="hidden"
            onChange={(e) => onFileChange(e, docId, side)}
            accept="image/*,application/pdf"
          />
        </div>
        {side && (
          <p className="mt-2 text-sm font-medium text-gray-500">{label}</p>
        )}
      </div>
    );
  };

  if (selectedDocument) {
    return (
      <DialogContent className="flex  flex-col sm:max-w-3xl">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBackToList}
              className="-ml-2 h-8 w-8 text-black01"
            >
              <ChevronLeft className="h-6 w-6" />
            </Button>
            <DialogTitle className="flex items-center gap-2 text-xl font-semibold text-black01">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white">
                <FileText className="h-4 w-4 text-gray-600" />
              </span>
              {selectedDocument.title}
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="flex flex-1 flex-col overflow-y-auto px-1 py-4">
          {selectedDocument.isIdDocument ? (
            <div className="flex gap-4">
              {renderUploadBox(selectedDocument.id, "Frente", "frente")}
              {renderUploadBox(selectedDocument.id, "Verso", "verso")}
            </div>
          ) : selectedDocument.isEnergyBill ? (
            <div className="flex flex-col gap-4">
              {renderUploadBox(
                selectedDocument.id,
                "Fatura de energia",
                "frente",
              )}
              {renderUploadBox(
                selectedDocument.id,
                "Fatura de energia (opcional)",
                "verso",
              )}
            </div>
          ) : (
            <div className="flex gap-4">
              {renderUploadBox(selectedDocument.id, selectedDocument.title)}
            </div>
          )}

          <div className="flex flex-col gap-2">
            {selectedDocument.files.map((file) => (
              <div
                key={file.id}
                className="flex items-center justify-between rounded-lg border p-3"
              >
                <div className="flex items-center gap-3">
                  {file.preview ? (
                    <img
                      src={file.preview}
                      className="h-10 w-10 rounded object-cover"
                    />
                  ) : (
                    <div className="flex h-10 w-10 items-center justify-center rounded bg-gray-100">
                      <FileText className="h-5 w-5 text-gray-500" />
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-black01">
                      {file.file.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {(file.file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemoveFile(selectedDocument.id, file.id)}
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            ))}
          </div>

          {/* {selectedDocument.isEnergyBill && (
              <>
                <div className="my-6 flex items-center gap-4">
                    <div className="h-[1px] flex-1 bg-gray-200" />
                    <span className="text-sm font-medium text-gray-400">Ou</span>
                    <div className="h-[1px] flex-1 bg-gray-200" />
                </div>

                    <div>
                        <h4 className="mb-1 text-base font-semibold text-black01">Documento sugeridos</h4>
                        <p className="mb-4 text-sm text-gray-500">Preencha os dados abaixo para verificar a disponibilidade</p>
                        
                        <div className="flex gap-4 overflow-x-auto pb-4 w-full mx-auto">
                            <div className="flex min-w-[280px] flex-col w-full overflow-hidden rounded-xl border border-gray-200">
                                <div className="bg-gray-100 p-4">
                                    <img src="https://placehold.co/400x250/e2e8f0/94a3b8?text=Fatura+de+energia" className="h-40 w-full object-cover rounded-lg mix-blend-multiply" alt="Suggestion" />
                                </div>
                            </div>
                        </div>
                    </div>
                </>
              )} */}
        </div>

        <div className="mt-4 border-t pt-4">
          <Button
            className="w-full rounded-full bg-primary font-bold text-white hover:bg-primary/90"
            onClick={handleBackToList}
          >
            Usar este documento
          </Button>
        </div>
      </DialogContent>
    );
  }

  return (
    <DialogContent className="flex min-h-[500px] flex-col sm:max-w-3xl">
      <DialogHeader>
        <div className="flex items-center gap-2">
          <DialogTitle className="text-xl font-semibold text-black01">
            Enviar documento
          </DialogTitle>
        </div>
      </DialogHeader>

      <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-1 py-4">
        {documents.map((doc) => {
          const hasFiles = doc.files.length > 0;
          const isComplete = doc.isIdDocument
            ? doc.files.length >= 2
            : hasFiles;

          return (
            <div
              key={doc.id}
              className={cn(
                "cursor-pointer rounded-xl border bg-white transition-all hover:border-gray-300",
                isComplete && "border-green-500 ring-1 ring-green-500",
              )}
              onClick={() => handleSelectDocument(doc.id)}
            >
              <div className="group flex items-center justify-between p-4">
                <div className="flex items-center gap-4">
                  <div
                    className={cn(
                      "flex h-12 w-12 items-center justify-center rounded-xl border",
                      isComplete
                        ? "border-green-100 bg-green-50 text-green-600"
                        : "border-gray-100 bg-gray-50 text-black01",
                    )}
                  >
                    {isComplete ? (
                      <Check className="h-6 w-6" />
                    ) : (
                      <FileText className="h-6 w-6" />
                    )}
                  </div>
                  <div>
                    <h4 className="font-semibold text-black01">{doc.title}</h4>
                    {doc.description && (
                      <p className="text-sm text-gray-500">{doc.description}</p>
                    )}
                    {/* File Preview Chips */}
                    {hasFiles && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {doc.files.map((f) => (
                          <div
                            key={f.id}
                            className="flex items-center gap-1 rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600"
                          >
                            <FileText className="h-3 w-3" />
                            <span className="max-w-[150px] truncate">
                              {f.file.name}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-gray-600" />
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex justify-end">
        <Button
          onClick={submitModal}
          className="w-[160px] rounded-full bg-primary font-semibold text-white hover:bg-primary/90"
          type="button"
          disabled={isLoading}
          isLoading={isLoading}
        >
          Avançar
        </Button>
      </div>
    </DialogContent>
  );
}
